/**
 * Frontend JavaScript for MR Blocks Modal
 * Simplified popup controller focused on onClick triggers
 */

class ModalController {
	constructor(element) {
		this.modal = element;
		this.settings = this.parseAttributes();
		this.isVisible = false;
		this.isDismissed = this.checkDismissalStatus();
		this.keyListener = null;

		// Initialize if not dismissed
		if (!this.isDismissed) {
			this.init();
		}
	}

	/**
	 * Parse data attributes from modal element
	 */
	parseAttributes() {
		const dataset = this.modal.dataset;
		return {
			id: dataset.id || this.modal.id,
			trigger: "onClick", // Modal is always onClick
			showClose: dataset.showClose === "true",
			dismiss: dataset.dismiss === "true",
			dismissTime: parseInt(dataset.dismissTime) || 1,
			anchorId: dataset.anchorId || "",
			anchorClose: dataset.anchorClose || "",
			outsideClose: dataset.outsideClose !== "false",
			lockScrolling: dataset.lockScrolling !== "false",
		};
	}

	/**
	 * Initialize modal
	 */
	init() {
		// Setup close button listeners
		this.setupCloseListeners();

		// Setup overlay click listener
		this.setupOverlayListener();

		// Setup keyboard listener
		this.setupKeyboardListener();

		// Setup anchor click listeners
		this.bindAnchors();
	}

	/**
	 * OnClick anchor trigger implementation
	 */
	bindAnchors() {
		if (!this.settings.anchorId) {
			console.warn("MR Blocks Modal: No anchor ID specified for modal trigger");
			return;
		}

		// Support multiple anchors (comma-separated)
		const anchorIds = this.settings.anchorId.split(",").map((id) => id.trim());

		anchorIds.forEach((anchorId) => {
			// Use event delegation for dynamic content
			document.addEventListener("click", (e) => {
				const target = e.target.closest(
					`#${anchorId}, [data-modal-anchor="${anchorId}"]`,
				);
				if (target) {
					e.preventDefault();
					this.showModal();
				}
			});
		});

		// Handle close anchors
		if (this.settings.anchorClose) {
			const closeIds = this.settings.anchorClose
				.split(",")
				.map((id) => id.trim());
			closeIds.forEach((closeId) => {
				document.addEventListener("click", (e) => {
					const target = e.target.closest(
						`#${closeId}, [data-modal-close="${closeId}"]`,
					);
					if (target) {
						e.preventDefault();
						this.hideModal();
					}
				});
			});
		}
	}

	/**
	 * Setup close button listeners
	 */
	setupCloseListeners() {
		const closeButtons = this.modal.querySelectorAll(
			".otter-popup__modal_close",
		);
		closeButtons.forEach((button) => {
			button.addEventListener("click", () => {
				this.hideModal();
			});
		});
	}

	/**
	 * Setup overlay click listener
	 */
	setupOverlayListener() {
		if (this.settings.outsideClose) {
			const overlay = this.modal.querySelector(
				".otter-popup__modal_wrap_overlay",
			);
			if (overlay) {
				overlay.addEventListener("click", (e) => {
					// Only close if clicking the overlay itself, not content
					if (e.target === overlay) {
						this.hideModal();
					}
				});
			}
		}
	}

	/**
	 * Setup keyboard listener (ESC key)
	 */
	setupKeyboardListener() {
		this.keyListener = (e) => {
			if (e.key === "Escape" && this.isVisible) {
				this.hideModal();
			}
		};

		document.addEventListener("keydown", this.keyListener);
	}

	/**
	 * Show modal with animation
	 */
	showModal() {
		if (this.isVisible || this.isDismissed) return;

		this.isVisible = true;
		this.modal.style.display = "flex";

		// Force reflow for animation
		this.modal.offsetHeight;

		// Add active class for animation
		this.modal.classList.add("is-active");

		// Lock body scroll if enabled
		if (this.settings.lockScrolling) {
			this.lockBodyScroll();
		}

		// Focus management for accessibility
		this.manageFocus();

		// Trigger custom event
		this.dispatchModalEvent("modal-shown");
	}

	/**
	 * Hide modal with animation
	 */
	hideModal() {
		if (!this.isVisible) return;

		this.isVisible = false;
		this.modal.classList.remove("is-active");

		// Wait for animation to complete before hiding
		setTimeout(() => {
			this.modal.style.display = "none";
		}, 250); // Slightly faster than popup

		// Unlock body scroll
		this.unlockBodyScroll();

		// Restore focus
		this.restoreFocus();

		// Handle dismissal
		if (this.settings.dismiss) {
			this.markAsDismissed();
		}

		// Trigger custom event
		this.dispatchModalEvent("modal-hidden");
	}

	/**
	 * Lock body scroll
	 */
	lockBodyScroll() {
		const scrollY = window.scrollY;
		document.body.style.position = "fixed";
		document.body.style.top = `-${scrollY}px`;
		document.body.style.width = "100%";
		document.body.classList.add("modal-open");

		// Store scroll position
		document.body.dataset.scrollY = scrollY;
	}

	/**
	 * Unlock body scroll
	 */
	unlockBodyScroll() {
		const scrollY = document.body.dataset.scrollY || "0";
		document.body.style.position = "";
		document.body.style.top = "";
		document.body.style.width = "";
		document.body.classList.remove("modal-open");

		// Restore scroll position
		window.scrollTo(0, parseInt(scrollY, 10));
		delete document.body.dataset.scrollY;
	}

	/**
	 * Focus management for accessibility
	 */
	manageFocus() {
		// Store currently focused element
		this.previousFocus = document.activeElement;

		// Focus the modal or first focusable element within it
		const focusableElements = this.modal.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);

		if (focusableElements.length > 0) {
			focusableElements[0].focus();
		} else {
			this.modal.focus();
		}
	}

	/**
	 * Restore focus to previous element
	 */
	restoreFocus() {
		if (this.previousFocus && typeof this.previousFocus.focus === "function") {
			this.previousFocus.focus();
		}
	}

	/**
	 * Check dismissal status from localStorage
	 */
	checkDismissalStatus() {
		if (!this.settings.dismiss || !this.settings.id) {
			return false;
		}

		try {
			const storageKey = "mr-modal-dismiss";
			const dismissedData = localStorage.getItem(storageKey);

			if (!dismissedData) return false;

			const dismissed = JSON.parse(dismissedData);
			const now = Date.now();

			// Clean expired entries
			const validEntries = dismissed.filter((entry) => entry.expiry > now);

			// Update storage with cleaned entries
			if (validEntries.length !== dismissed.length) {
				localStorage.setItem(storageKey, JSON.stringify(validEntries));
			}

			// Check if this modal is dismissed
			return validEntries.some((entry) => entry.modalID === this.settings.id);
		} catch (error) {
			console.warn("MR Blocks Modal: Could not access localStorage", error);
			return false;
		}
	}

	/**
	 * Mark modal as dismissed
	 */
	markAsDismissed() {
		if (!this.settings.dismiss || !this.settings.id) return;

		try {
			const storageKey = "mr-modal-dismiss";
			const expiryTime =
				Date.now() + this.settings.dismissTime * 24 * 60 * 60 * 1000;

			let dismissed = [];
			const existingData = localStorage.getItem(storageKey);

			if (existingData) {
				dismissed = JSON.parse(existingData);
			}

			// Remove existing entry for this modal
			dismissed = dismissed.filter(
				(entry) => entry.modalID !== this.settings.id,
			);

			// Add new dismissal entry
			dismissed.push({
				modalID: this.settings.id,
				expiry: expiryTime,
			});

			localStorage.setItem(storageKey, JSON.stringify(dismissed));
			this.isDismissed = true;
		} catch (error) {
			console.warn("MR Blocks Modal: Could not save dismissal state", error);
		}
	}

	/**
	 * Dispatch custom modal events
	 */
	dispatchModalEvent(eventName) {
		const event = new CustomEvent(eventName, {
			detail: {
				modal: this.modal,
				settings: this.settings,
			},
		});

		document.dispatchEvent(event);
	}

	/**
	 * Cleanup method for removing event listeners
	 */
	destroy() {
		if (this.keyListener) {
			document.removeEventListener("keydown", this.keyListener);
		}

		// Restore scroll if modal was open
		if (this.isVisible) {
			this.unlockBodyScroll();
		}
	}
}

/**
 * Initialize all modals when DOM is ready
 */
function initializeModals() {
	const modals = document.querySelectorAll(".wp-block-mr-blocks-modal");

	modals.forEach((modal) => {
		// Avoid double initialization
		if (!modal.dataset.mrModalInitialized) {
			new ModalController(modal);
			modal.dataset.mrModalInitialized = "true";
		}
	});
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initializeModals);
} else {
	initializeModals();
}

// Expose for debugging
window.MRModalController = ModalController;
